import { Form, FormTagsInput, FormTextInput, StandardFormButtons } from '@components/form';
import { useFileUpload } from '@fileUpload/FileUpload';
import {
  Skill,
  useCompleteSkillUploadMutation,
  useEditSkillMutation,
  useReplaceSkillPackageMutation,
} from '@generated/graphql';
import { FileWithPath } from '@helper-functions/fileSystemTraversal';
import { zipFiles } from '@helper-functions/zipFiles';
import { Divider, Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { FormikHelpers } from 'formik';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as yup from 'yup';
import { SkillFileField, UploadState } from './SkillFileField';

const validationSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .max(64, 'Name must be 64 characters or less')
    .matches(/^[a-z0-9-]+$/, 'Name can only contain lowercase letters, numbers, and hyphens'),
  description: yup.string(),
  version: yup.string(),
  tags: yup.array().of(yup.string()),
});

type SkillEditModalProps = {
  skill: Pick<Skill, 'name' | 'skillId' | 'version' | 'config' | 'description'>;
  onClose: VoidFunction;
  onEdit: VoidFunction;
};

export const SkillEditModal = ({ skill, onClose, onEdit }: SkillEditModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const [editSkill] = useEditSkillMutation();
  const [replaceSkillPackage] = useReplaceSkillPackageMutation();
  const [completeSkillUpload] = useCompleteSkillUploadMutation();
  const uploadFile = useFileUpload();

  const [formError, setFormError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSubmit = createAsyncNotification(
    'Skill updated successfully.',
    async (
      {
        name,
        description,
        version,
        tags,
        file,
      }: {
        name: string;
        description: string;
        version: string;
        tags: string[];
        file: File | FileWithPath[] | null;
      },
      helpers: FormikHelpers<{
        name: string;
        description: string;
        version: string;
        tags: string[];
        file: File | FileWithPath[] | null;
      }>,
    ) => {
      helpers.setSubmitting(true);

      if (!skill.skillId) {
        throw new Error('Skill ID is required');
      }

      try {
        if (file) {
          let fileToUpload: File | Blob;

          // If folder selected, zip it client-side first
          if (Array.isArray(file)) {
            setUploadState('zipping');
            fileToUpload = await zipFiles(file);
          } else {
            fileToUpload = file;
          }

          setUploadState('uploading');
          const { data } = await replaceSkillPackage({
            variables: { skillId: skill.skillId, size: fileToUpload.size },
          });

          if (!data?.replaceSkillPackage) {
            throw new Error('Failed to get upload URLs for package replacement');
          }

          const { uploadId, partSize, urls } = data.replaceSkillPackage;

          // Upload to S3 (handles multipart chunking, progress, retries)
          const etags = await uploadFile({
            file: fileToUpload as File,
            urls,
            partSize,
            onUploadProgress: setUploadProgress,
          });

          // Finalize multipart upload (no-op for single-part)
          setUploadState('completing');
          await completeSkillUpload({
            variables: { skillId: skill.skillId, uploadId, parts: etags },
          });

          setUploadState('idle');
        }
        await editSkill({
          variables: {
            skillId: skill.skillId,
            name,
            description,
            version,
            config: tags.length > 0 ? JSON.stringify({ tags }) : undefined,
          },
          onCompleted: () => {
            posthog.capture('workspace:user_edit-workspace-skill', {
              skillId: skill.skillId,
            });
            onEdit?.();
            onClose();
          },
          onError: (error) => {
            setFormError(error.message);
            throw error;
          },
        });
      } catch (err: unknown) {
        setUploadState('error');
        setFormError((err as Error)?.message || 'An error occurred during upload');
      } finally {
        helpers.setSubmitting(false);
      }
    },
    {
      loadingMessage: 'Updating skill...',
    },
  );

  return (
    <Form
      initialValues={{
        name: skill.name || '',
        description: skill.description || '',
        version: skill.version || '',
        tags: skill.config || [],
        file: null,
        fileIsZip: false,
      }}
      validationSchema={validationSchema}
      validateOnBlur
      onSubmit={handleSubmit}
    >
      {() => {
        return (
          <Stack>
            <FormTextInput
              name="name"
              label="Name"
              helperText="Lowercase letters, numbers, and hyphens only (max 64 characters)"
              required
            />
            <FormTextInput
              name="description"
              label="Description (optional)"
              placeholder="Brief description of what this skill does"
              multiline
              rows={3}
            />
            <FormTextInput name="version" label="Version (optional)" placeholder="1.0.0" />
            <FormTagsInput name="tags" label="Tags (optional)" />
            <Divider flexItem sx={{ mb: 5 }} />
            <SkillFileField
              name="file"
              label="Replace skill package (optional)"
              uploadState={uploadState}
              uploadProgress={uploadProgress}
            />
            <StandardFormButtons onClose={onClose} formError={formError} submitLabel="Save skill" />
          </Stack>
        );
      }}
    </Form>
  );
};
